import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, ShieldCheck, Images, ChevronRight, Sparkles } from "lucide-react";
import { GoogleIcon, Spinner } from "@/components/peeps/ui";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, themeOf, gradientCss } from "@/lib/theme";
import { formatDate } from "@/lib/date";
import { TabLayout } from "@/components/peeps/TabLayout";
import logo from "@/assets/peeps-logo.png";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      setGroups(await api.listGroups());
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <TabLayout>
      <div className="px-4 pt-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <button data-testid="home-peeps-button" onClick={() => navigate("/")} aria-label="Home" className="transition-transform active:scale-90">
              <img src={logo} alt="peeps" className="w-11 h-11 rounded-xl" />
            </button>
            <div>
              <button data-testid="profile-name-link" onClick={() => navigate("/profile")} className="font-display font-medium text-[13px] text-left" style={{ color: colors.brandSecondary }}>Hey {user?.display_name || "there"}</button>
              <h1 className="font-display font-bold text-[26px]" style={{ color: colors.onSurface }}>Your groups</h1>
            </div>
          </div>
          <button data-testid="create-group-button" onClick={() => navigate("/create-group")} className="p-2.5 rounded-xl mr-14" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <Plus size={22} color={colors.brand} />
          </button>
        </div>

        <button data-testid="all-photos-link" onClick={() => navigate("/gallery/all")} className="flex items-center gap-3 w-full p-3 rounded-2xl mb-5 text-left transition-colors hover:border-[#4A4B5C]" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: colors.brandTertiary }}>
            <Images size={18} color={colors.brandSecondary} />
          </div>
          <div className="flex-1">
            <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>All photos</p>
            <p className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Every memory across your groups</p>
          </div>
          <ChevronRight size={16} color={colors.muted} />
        </button>

        {loading ? (
          <div className="flex justify-center pt-20"><Spinner /></div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center pt-20 px-6 text-center gap-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: colors.brandTertiary }}>
              <Sparkles size={30} color={colors.brandSecondary} />
            </div>
            <h2 className="font-display font-bold text-xl" style={{ color: colors.onSurface }}>No plans yet</h2>
            <p className="font-display text-sm" style={{ color: colors.onSurfaceTertiary }}>Create a group to start planning with your people.</p>
            <button data-testid="empty-create-group" onClick={() => navigate("/create-group")} className="flex items-center gap-1.5 mt-3 py-3 px-5 rounded-xl font-display font-bold text-sm" style={{ background: colors.brand, color: colors.onBrand }}>
              <Plus size={18} /> New group
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((item, index) => {
              const th = themeOf(item.theme);
              const cover = mediaUrl(item.cover_image_url);
              return (
                <div
                  key={item.id}
                  data-testid={`group-card-${item.id}`}
                  onClick={() => navigate(`/group/${item.id}`)}
                  className="rounded-2xl overflow-hidden cursor-pointer animate-fade-up transition-all hover:border-[#4A4B5C]"
                  style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, animationDelay: `${index * 55}ms` }}
                >
                  <div className="relative h-32 flex flex-col justify-end" style={cover ? {} : { background: gradientCss(th.grad) }}>
                    {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(21,22,30,0.1), rgba(21,22,30,0.92))" }} />
                    <div className="relative p-3 flex flex-col gap-2">
                      <h3 className="font-display font-bold text-xl text-white">{item.name}</h3>
                      <div className="flex gap-2">
                        <span className="flex items-center gap-1 py-1 px-2.5 rounded-full" style={{ background: "rgba(21,22,30,0.55)" }}>
                          <Users size={12} color={colors.onSurfaceSecondary} />
                          <span className="font-mono2 font-medium text-[11px]" style={{ color: colors.onSurfaceSecondary }}>{item.member_count}</span>
                        </span>
                        {item.my_role === "admin" && (
                          <span className="flex items-center gap-1 py-1 px-2.5 rounded-full" style={{ background: "rgba(21,22,30,0.55)" }}>
                            <ShieldCheck size={12} color={th.solid} />
                            <span className="font-mono2 font-medium text-[11px]" style={{ color: th.solid }}>Admin</span>
                          </span>
                        )}
                        {item.google_synced && (
                          <span className="flex items-center gap-1 py-1 px-2.5 rounded-full" style={{ background: "rgba(21,22,30,0.55)" }}>
                            <GoogleIcon size={11} />
                            <span className="font-mono2 font-medium text-[11px]" style={{ color: colors.onSurfaceSecondary }}>Synced</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    {item.next_event ? (
                      <p className="flex-1 font-display text-[13px] truncate" style={{ color: colors.onSurfaceTertiary }}>
                        Next: <span className="font-bold" style={{ color: colors.onSurface }}>{item.next_event.title}</span> · {formatDate(item.next_event.date)}
                      </p>
                    ) : (
                      <p className="flex-1 font-display text-[13px]" style={{ color: colors.faint }}>No plans yet — be the instigator</p>
                    )}
                    <ChevronRight size={16} color={colors.muted} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TabLayout>
  );
}
